import React, {
  createContext,
  useContext,
  useRef,
  useState,
  useEffect,
  useMemo,
} from "react";
import B2cReceived from "./B2cReceivedOrder";
import B2cConfirmOrder from "./B2cConfirmOrder";
import B2cPackedOrder from "./B2cPackedOrder";
import B2cCancelledOrder from "./B2cCancelledOrder";
import B2cShipmentRecevied from "./B2cShipmentRecevied";
import apiService from "../../../Services/authenticationServices";
import { ChevronDown, Search } from "lucide-react";
import { PageHeader, Tabs } from "../../ui/caliper";

const FILTER_INPUT =
  "h-8 px-2.5 border border-hairline rounded-cb-sm bg-panel text-t-md text-fg-default placeholder:text-fg-subtle outline-none focus:border-ink-500 focus:shadow-cb-focus transition-shadow";

const B2cOrdersContext = createContext();

/* Filter state -> the query the tabs send: warehouses joined. The tabs drop blanks. */
const toApiFilter = (filter) => ({
  ...filter,
  circleCode: filter.circleCode.join(","),
});

/* How long typing in a filter waits before the list reloads. Search applies at once. */
const FILTER_DEBOUNCE_MS = 400;

export const useB2cOrders = () => useContext(B2cOrdersContext);

const B2cOrders = () => {
  const [activeTab, setActiveTab] = useState("received");
  const childRef = useRef();
  const user = JSON.parse(localStorage.getItem("loginObj"));
  const circleCode = localStorage.getItem("circleCode");
  const [defaultWHList, setdefaultWHList] = useState([]);
  const [warehouseList, setwarehouseList] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const warehouseDropdownRef = useRef(null);

  const [loggedInUser] = useState(
    user.userRolesList.find(
      (user) => user.roleCode === "PHHEAD" || user.roleCode === "PH"
    )
  );
  const [selectedSource, setselectedSource] = useState("CHE");

  const stateList = [
    { name: 'Odisha', value: 'OD' },
    { name: 'Chhattisgarh', value: 'CG' },
    { name: 'Jharkhand', value: 'JH' }
  ];

  const [filter, setFilter] = useState({
    state:
      loggedInUser?.roleCode !== "PHHEAD"
        ? localStorage.getItem("userState")
        : "OD",
    circleCode: circleCode != null ? [circleCode] : ["N"],
    enquiryId: "",
    orderId: "",
    mobileNo: "",
    fromDate: "",
    toDate: "",
    clinic: "",
  });

  const [dateRangeError, setDateRangeError] = useState(false);

  /* The filters the tabs actually load with. Typing updates `filter` at once (so the
     inputs stay responsive) and this copy after a short pause, so a 13-digit order
     ID is one request rather than thirteen. */
  const [appliedFilter, setAppliedFilter] = useState(filter);
  useEffect(() => {
    const t = setTimeout(() => setAppliedFilter(filter), FILTER_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [filter]);
  /* Rebuilt only when a filter value changes. A new object on every render made
     each tab reload on any re-render of this page (opening the warehouse dropdown,
     the warehouse list arriving). */
  const appliedKey = JSON.stringify(appliedFilter);
  const apiFilter = useMemo(() => toApiFilter(JSON.parse(appliedKey)), [appliedKey]);

  const setFilterField = (field, value) => {
    setFilter((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const resetFilterField = (field) => {
    if (field === "circleCode") {
      setFilter((prev) => ({
        ...prev,
        [field]: [],
      }));
    } else {
      setFilter((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const resetAllFilters = () => {
    const next = {
      state:
        loggedInUser?.roleCode !== "PHHEAD"
          ? localStorage.getItem("userState")
          : filter?.state
            ? filter?.state
            : "OD",
      circleCode: circleCode != null ? [circleCode] : ["N"],
      enquiryId: "",
      orderId: "",
      mobileNo: "",
      fromDate: "",
      toDate: "",
      clinic: "",
    };
    setFilter(next);
    /* A reset applies at once — no pause, no load with the old filters. */
    setAppliedFilter(next);
    setDateRangeError(false);
  };

  /* Reset in the same update as the tab change, so the new tab's first load
     already uses the cleared filters instead of the previous tab's. */
  const handleTabChange = (id) => {
    if (id === activeTab) return;
    resetAllFilters();
    setActiveTab(id);
  };

  const tabs = [
    { id: "received", label: "Received Orders" },
    { id: "confirm", label: "Confirm Orders" },
    // { id: "shipment", label: "Shipment Received" },
    { id: "packed", label: "Packed Orders" },
    { id: "cancelled", label: "Cancelled Orders" },
  ];

  useEffect(() => {
    resetAllFilters();
    wareHouseData();
  }, [activeTab]);

  const renderTabContent = () => {
    switch (activeTab) {
      case "received":
        return <B2cReceived ref={childRef} />;
      case "confirm":
        return <B2cConfirmOrder ref={childRef} />;
      case "shipment":
        return <B2cShipmentRecevied ref={childRef} />;
      case "packed":
        return <B2cPackedOrder ref={childRef} />;
      case "cancelled":
        return <B2cCancelledOrder ref={childRef} />;
      default:
        return null;
    }
  };

  const validateDateRange = (fromDate, toDate) => {
    /* One date alone is valid (the API reads it as that day); only a full range
       can exceed the limit. */
    if (!fromDate || !toDate) return true;
    const start = new Date(fromDate);
    const end = new Date(toDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 14;
  };

  const handleFilterChange = (field, value) => {
    if (field === "fromDate" || field === "toDate") {
      const isValidRange = validateDateRange(
        field === "fromDate" ? value : filter.fromDate,
        field === "toDate" ? value : filter.toDate
      );
      setDateRangeError(!isValidRange);
    }

    setFilter((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleReset = (field) => {
    if (field === "fromDate" || field === "toDate") {
      setDateRangeError(false); // one date left, so no range to exceed
    }
    setFilter((prev) => ({
      ...prev,
      [field]: "",
    }));
  };

  const handleSearch = () => {
    if (dateRangeError) {
      return; // Prevent search if date range is invalid
    }
    /* Filters typed but not yet applied: apply them now — the tab reloads when its
       filter changes. Otherwise reload the current filters. */
    if (JSON.stringify(filter) !== appliedKey) {
      setAppliedFilter(filter);
      return;
    }
    if (childRef.current?.onSearch) {
      childRef.current.onSearch();
    }
  };

  const wareHouseData = async () => {
    try {
      const response = await apiService.getwareHouseDetails();
      const res = response.data.successList;
      setdefaultWHList(res);
      setwarehouseList(res.filter((e) => e.stateCode == filter.state));
    } catch (err) {
      console.error("Error fetching order details:", err);
    }
  };

  useEffect(() => {
    setwarehouseList(defaultWHList.filter((e) => e.stateCode == filter.state));
  }, [filter.state]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        warehouseDropdownRef.current &&
        !warehouseDropdownRef.current.contains(event.target)
      ) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="flex flex-col gap-4 cb-fade-in">
      <PageHeader
        title="B2C orders"
        sub="Direct consumer orders — home delivery"
      />

      <div className="flex min-w-0">
        <div className="flex flex-wrap items-center gap-2 w-full bg-panel border border-hairline rounded-cb-md px-3 py-2.5">
          {loggedInUser?.roleCode === "PHHEAD" && (
            <>
              <select
                value={filter.state}
                onChange={(e) => handleFilterChange("state", e.target.value)}
                className={`${FILTER_INPUT} w-full sm:w-44`}
              >
                <option value="">Select state</option>
                {stateList.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.name}
                  </option>
                ))}
              </select>

              <div className="flex items-center gap-2">
                <div className="relative w-60" ref={warehouseDropdownRef}>
                  <div
                    className={`${FILTER_INPUT} flex items-center justify-between cursor-pointer`}
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                  >
                    <span className="truncate">
                      {filter.circleCode.length > 0
                        ? `${filter.circleCode.length} warehouse(s) selected`
                        : "Select warehouses"}
                    </span>
                    <ChevronDown
                      size={14}
                      strokeWidth={1.9}
                      className={`ml-2 text-fg-muted transition-transform ${dropdownOpen ? "rotate-180" : ""}`}
                    />
                  </div>
                  {dropdownOpen && (
                    <div className="absolute z-50 w-full mt-1 bg-panel border border-hairline rounded-cb-md shadow-cb-md max-h-60 overflow-y-auto cb-scroll py-1">
                      {warehouseList.map((warehouse) => {
                        const checked = filter.circleCode.includes(
                          warehouse.circleCode
                        );
                        return (
                          <label
                            key={warehouse.pharmacyCode}
                            className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-surface-hover text-t-sm"
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => {
                                let updated = [...filter.circleCode];
                                if (e.target.checked) {
                                  updated.push(warehouse.circleCode);
                                } else {
                                  updated = updated.filter(
                                    (c) => c !== warehouse.circleCode
                                  );
                                }
                                setFilter((prev) => ({
                                  ...prev,
                                  circleCode: updated,
                                }));
                              }}
                              className="accent-ink-600"
                            />
                            {warehouse.pharmacyName}
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
                {filter.circleCode.length > 0 && (
                  <button
                    onClick={() => resetFilterField("circleCode")}
                    className="text-red-700 text-t-xs font-semibold hover:underline whitespace-nowrap"
                  >
                    Reset
                  </button>
                )}
              </div>
            </>
          )}

          {(selectedSource === "CHE" && activeTab !== "enquiry") ||
            selectedSource === "B2C" ? (
            <input
              type="text"
              placeholder="Order ID"
              value={filter.orderId}
              onChange={(e) => handleFilterChange("orderId", e.target.value)}
              className={`${FILTER_INPUT} w-full sm:w-44`}
            />
          ) : null}

          <div className={`${FILTER_INPUT} flex items-center w-full sm:w-52 relative ${dateRangeError ? "border-red-500" : ""}`}>
            <input
              type="text"
              onFocus={(e) => {
                e.target.type = "date";
                e.target.showPicker?.();
              }}
              onBlur={(e) => {
                if (!e.target.value) {
                  e.target.type = "text";
                }
              }}
              value={filter.fromDate || ""}
              onChange={(e) => {
                if (e.target.type === "date") {
                  handleFilterChange("fromDate", e.target.value);
                }
              }}
              className="flex-1 min-w-0 outline-none bg-transparent text-fg-default placeholder:text-fg-subtle"
              placeholder="From date"
            />
            {filter.fromDate && (
              <button
                onClick={() => handleReset("fromDate")}
                className="text-red-700 text-t-xs font-semibold hover:underline ml-2"
              >
                Reset
              </button>
            )}
          </div>

          <div className={`${FILTER_INPUT} flex items-center w-full sm:w-52 relative ${dateRangeError ? "border-red-500" : ""}`}>
            <input
              type={filter.toDate ? "date" : "text"}
              value={filter.toDate || ""}
              onChange={(e) => handleFilterChange("toDate", e.target.value)}
              onFocus={(e) => {
                if (!filter.toDate) {
                  e.target.type = "date";
                }
              }}
              onBlur={(e) => {
                if (!e.target.value) {
                  e.target.type = "text";
                }
              }}
              className="flex-1 min-w-0 outline-none bg-transparent text-fg-default placeholder:text-fg-subtle"
              placeholder="To date"
              min={filter.fromDate}
            />
            {filter.toDate && (
              <button
                onClick={() => handleReset("toDate")}
                className="text-red-700 text-t-xs font-semibold hover:underline ml-2"
              >
                Reset
              </button>
            )}
          </div>
          {dateRangeError && (
            <div className="text-red-700 text-t-xs w-full">
              Date range cannot exceed 2 weeks (14 days).
            </div>
          )}

          <button
            onClick={handleSearch}
            className="ml-auto inline-flex items-center gap-1.5 h-8 px-4 bg-ink-600 text-fg-on-ink text-t-md font-medium rounded-cb-sm hover:bg-ink-700 active:bg-ink-800 transition-colors"
          >
            <Search size={14} strokeWidth={1.9} />
            Search
          </button>
        </div>
      </div>

      <Tabs
        tabs={tabs}
        value={activeTab}
        onChange={handleTabChange}
        className="-mt-1"
      />

      <B2cOrdersContext.Provider
        value={{
          filter: apiFilter,
          setFilterField,
          resetFilterField,
          resetAllFilters,
        }}
      >
        <div>{renderTabContent()}</div>
      </B2cOrdersContext.Provider>
    </div>
  );
};

export default B2cOrders;
